import i18n, { type ParseKeys } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

import alertEn from './locales/en/alert.json';
import appEn from './locales/en/app.json';
import commonEn from './locales/en/common.json';
import dashboardEn from './locales/en/dashboard.json';
import deviceEn from './locales/en/device.json';
import elabelEn from './locales/en/elabel.json';
import nativeEn from './locales/en/native.json';
import userManagementEn from './locales/en/userManagement.json';
import alertZh from './locales/zh-HK/alert.json';
import appZh from './locales/zh-HK/app.json';
import commonZh from './locales/zh-HK/common.json';
import dashboardZh from './locales/zh-HK/dashboard.json';
import deviceZh from './locales/zh-HK/device.json';
import elabelZh from './locales/zh-HK/elabel.json';
import nativeZh from './locales/zh-HK/native.json';
import userManagementZh from './locales/zh-HK/userManagement.json';

export const SUPPORTED_LANGUAGES = ['en', 'zh-HK'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const I18N_NAMESPACES = [
  'common',
  'app',
  'dashboard',
  'device',
  'elabel',
  'alert',
  'userManagement',
  'native',
] as const;

/** Every valid namespaced key (e.g. `alert:nav.alerts`). For keys stored outside components. */
export type AnyTranslationKey = ParseKeys<typeof I18N_NAMESPACES>;

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  'zh-HK': '繁體中文',
};

const resources = {
  en: {
    alert: alertEn,
    app: appEn,
    common: commonEn,
    dashboard: dashboardEn,
    device: deviceEn,
    elabel: elabelEn,
    native: nativeEn,
    userManagement: userManagementEn,
  },
  'zh-HK': {
    alert: alertZh,
    app: appZh,
    common: commonZh,
    dashboard: dashboardZh,
    device: deviceZh,
    elabel: elabelZh,
    native: nativeZh,
    userManagement: userManagementZh,
  },
};

// Any Chinese variant gets Traditional (zh-HK) rather than English — closer for
// this user base.
const convertDetectedLanguage = (detected: string): string => {
  const lng = detected.toLowerCase();
  if (lng.startsWith('zh')) {
    return 'zh-HK';
  }
  return 'en';
};

interface InitOptions {
  // Tests force a language and skip detection (jsdom navigator is meaningless).
  forceLanguage?: SupportedLanguage;
}

const registerResourceBundles = () => {
  for (const [lng, bundles] of Object.entries(resources)) {
    for (const [ns, bundle] of Object.entries(bundles)) {
      i18n.addResourceBundle(lng, ns, bundle, true, true);
    }
  }
};

const applyDocumentLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
};

export const initI18n = ({ forceLanguage }: InitOptions = {}) => {
  // HMR re-evaluates this module with fresh bundles; merge them into the
  // singleton so newly added keys appear without a full page reload.
  if (i18n.isInitialized) {
    registerResourceBundles();
    return i18n;
  }

  const base = i18n.use(initReactI18next);
  const configured = forceLanguage ? base : base.use(LanguageDetector);
  i18n.on('languageChanged', applyDocumentLang);

  void configured.init({
    resources,
    ...(forceLanguage ? { lng: forceLanguage } : {}),
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: [...I18N_NAMESPACES],
    supportedLngs: [...SUPPORTED_LANGUAGES],
    nonExplicitSupportedLngs: false,
    interpolation: { escapeValue: false },
    returnNull: false,
    react: { useSuspense: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'els.language',
      convertDetectedLanguage,
    },
  });

  return i18n;
};

export { i18n };
